/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

/**
 * This file contains the generic types for the API between a general chat widget and a chat back-end. It is
 * intended to provide base types for a standalone widget and should not contain any imports of other types.
 */

import { DeepPartial } from "../utilities/DeepPartial";
import { MessageErrorState } from "./LocalMessageItem";
import { HumanAgentsOnlineStatus } from "../config/ServiceDeskConfig";
import { FileStatusValue } from "../config/ServiceDeskConfig";
import {
  BUTTON_KIND,
  BUTTON_SIZE,
} from "@carbon/web-components/es-custom/components/button/defs.js";
import {
  CHAT_BUTTON_KIND,
  CHAT_BUTTON_SIZE,
} from "@carbon/ai-chat-components/es-custom/react/chat-button.js";
import { ChainOfThoughtStepStatus } from "@carbon/ai-chat-components/es-custom/components/chain-of-thought/defs.js";
import { WorkspaceCustomPanelConfigOptions } from "../instance/apiTypes";

/**
 * This is the main interface that represents a request from a user sent to an assistant.
 *
 * @category Messaging
 */
interface MessageRequest<TInputType extends BaseMessageInput = MessageInput> {
  /**
   * The unique identifier for this request object. This value may be assigned by the client when a request is
   * made but should be assigned by the service if one is not provided.
   */
  id?: string;

  /**
   * The history information to store as part of this request. This includes extra information that was provided to
   * the user and about the user that was used in making the request.
   */
  history?: MessageRequestHistory;

  /**
   * Used to store private state information about the message.
   *
   * @internal
   */
  ui_state_internal?: MessageUIStateInternal;

  /**
   * The input data to the back-end to make in this request.
   */
  input: TInputType;

  /**
   * Optional context which is added from external resources.
   */
  context?: unknown;

  /**
   * The ID of the thread this request belongs to. This is here to prepare for input message editing and regenerating
   * responses.
   */
  thread_id?: string;

  /**
   * The parent ID of the thread this request belongs to. This is here to prepare for input message editing and regenerating
   * responses.
   */
  parent_thread_id?: string;
}

/**
 * The set of possible message input types in a request.
 *
 * @category Messaging
 */
enum MessageInputType {
  /**
   * Represents a simple text message.
   */
  TEXT = "text",

  /**
   * Represents an event message that can be used to send control, updates, or action information to the back-end.
   */
  EVENT = "event",
}

/**
 * These are custom message types that are not part of the api but are generated by the widget for internal use.
 *
 * @category Messaging
 */
enum InternalMessageRequestType {
  FILE = "file",
}

/**
 * Types of internal system messages generated by the chat system.
 * @internal
 * @category Messaging
 */
export enum InternalSystemMessageType {
  /**
   * Indicates a request was cancelled before receiving a response.
   */
  REQUEST_CANCELLED = "request_cancelled",

  /**
   * Indicates a streaming response was stopped mid-stream.
   */
  RESPONSE_STOPPED = "response_stopped",
}

/**
 * @category Messaging
 */
interface BaseMessageInput {
  /**
   * The type of user input.
   */
  message_type?: MessageInputType;
}

/**
 * Base interface for an event message that can be used to send control, updates, or action information to the back-end.
 *
 * @category Messaging
 */
interface EventInput<
  TEventInputType = EventInputData,
> extends BaseMessageInput {
  /**
   * Event messages have this as their input type.
   */
  message_type: MessageInputType.EVENT;

  /**
   * The type of the event.
   */
  event: TEventInputType;
}

/**
 * Input for an event. The name of the event is mandatory. Additional fields depend on the event.
 *
 * @template TNameType The type of the name property for the event. This can just be a string or it can be a
 * specific string in order to create type safety ensuring each event has the right name (e.g. "typeof MY_EVENT_NAME").
 *
 * @category Messaging
 */
interface EventInputData<TNameType extends string = string> {
  /**
   * The name of the event.
   */
  name: TNameType;
}

/**
 * The type of a structured field.
 *
 * @experimental
 * @category Messaging
 */
type StructuredFieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "select"
  | "multi_select"
  | "date"
  | "datetime"
  | "file"
  | "unknown";

/**
 * Represents an inline file — the actual File object to be uploaded.
 * Use this when the file needs to be uploaded as part of the message send.
 * The widget passes this through to customSendMessage unchanged; actual upload
 * handling is the responsibility of the customSendMessage implementation.
 *
 * @experimental
 * @category Messaging
 */
interface InlineFile {
  /**
   * Type discriminator.
   */
  type: "inline";

  /**
   * The actual File object.
   */
  file: File;

  /**
   * Optional unique ID for tracking.
   */
  id?: string;

  /**
   * Optional upload status (for UI feedback).
   */
  status?: FileStatusValue;

  /**
   * Optional error information.
   */
  error?: {
    message: string;
  };
}

/**
 * Represents an external file reference — a file already uploaded elsewhere.
 * Use this when files are uploaded separately and you just need to reference them.
 *
 * @experimental
 * @category Messaging
 */
interface ExternalFileReference {
  /**
   * Type discriminator.
   */
  type: "reference";

  /**
   * File identifier (could be a database ID, UUID, etc.).
   */
  id: string;

  /**
   * Optional URL to the file.
   */
  url?: string;

  /**
   * Optional filename for display.
   */
  name?: string;

  /**
   * Optional MIME type.
   */
  mime_type?: string;

  /**
   * Optional file size in bytes.
   */
  size?: number;
}

/**
 * Represents a file value in a structured field. Can be either an inline File
 * object or a reference to an already-uploaded file.
 *
 * @experimental
 * @category Messaging
 */
type FileFieldValue = InlineFile | ExternalFileReference;

/**
 * The value of a structured field. The actual type depends on the field's
 * {@link StructuredFieldType}.
 *
 * @experimental
 * @category Messaging
 */
type StructuredFieldValue =
  | string // text, textarea, select, date, datetime
  | number // number
  | boolean // boolean
  | string[] // multi_select
  | FileFieldValue // file (single)
  | FileFieldValue[] // file (multiple)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | any; // unknown / user_defined escape hatch

/**
 * A single typed field within a {@link StructuredData} payload.
 *
 * @experimental
 * @category Messaging
 */
interface StructuredField {
  /**
   * Unique identifier for this field.
   */
  id: string;

  /**
   * Human-readable label (optional).
   */
  label?: string;

  /**
   * The type of field.
   */
  type?: StructuredFieldType;

  /**
   * The value of the field.
   */
  value: StructuredFieldValue;
}

/**
 * Structured data that can be sent alongside or instead of plain text input.
 * Supports typed fields for common input patterns (text, select, multi-select,
 * file, etc.) as well as an escape hatch for arbitrary user-defined data.
 *
 * @experimental
 * @category Messaging
 */
interface StructuredData {
  /**
   * Typed fields with known structures (recommended for most use cases).
   */
  fields?: StructuredField[];

  /**
   * Escape hatch: arbitrary key-value data for user-defined implementations.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user_defined?: Record<string, any>;
}

/**
 * The default interface for message input that is sent to an assistant in a message request. This represents basic text
 * input.
 *
 * @category Messaging
 */
interface MessageInput extends BaseMessageInput {
  /**
   * The text of the user input to send to the back-end.
   */
  text?: string;

  /**
   * For messages that are sent between the user and a human agent, we assign an agent type to the message to distinguish what type it is.
   */
  agent_message_type?: HumanAgentMessageType;

  /**
   * Structured data that can be sent alongside or instead of plain text input.
   * Supports typed fields (text, select, multi-select, file, etc.) and an
   * escape hatch for arbitrary user-defined data.
   *
   * @experimental
   */
  structured_data?: StructuredData;
}

/**
 * This interface represents the main response content that is received by a client from an assistant. It is generally
 * in response to a previous message request.
 *
 * @category Messaging
 */
interface MessageResponse<TGenericType = GenericItem[]> {
  /**
   * A unique identifier for this response object.
   */
  id?: string;

  /**
   * The id of the request that this is the response of.
   */
  request_id?: string;

  /**
   * The output from the back-end to be rendered or processed by the client.
   */
  output: MessageOutput<TGenericType>;

  /**
   * The context information returned by the back-end.
   */
  context?: unknown;

  /**
   * The ID of the thread this request belongs to. This is here to prepare for input message editing and regenerating
   * responses.
   */
  thread_id?: string;

  /**
   * Used to store private state information about the message.
   *
   * @internal
   */
  ui_state_internal?: MessageUIStateInternal;

  /**
   * The history information to store as part of this request.
   */
  history?: MessageResponseHistory;

  /**
   * Options for the {@link MessageResponse}. This includes metadata about the user or assistant sending this response.
   */
  message_options?: MessageResponseOptions;
}

/**
 * The output context for a message response from an assistant.
 *
 * @category Messaging
 */
interface MessageOutput<TGenericType = GenericItem[]> {
  /**
   * Responses intended to be processed by a generic channel. This will be an array of message response items.
   */
  generic?: TGenericType;
}

/**
 * The set of possible message types in a response.
 *
 * @category Messaging
 */
enum MessageResponseTypes {
  /**
   * Represents a basic text response. The given text may contain rich content such as markdown.
   */
  TEXT = "text",

  /**
   * A response that requests the user choose an option from a list. The list of options may be presented as a list
   * of buttons or it may be from a drop-down.
   */
  OPTION = "option",

  /**
   * Indicates that the conversation should be escalated to a human agent and offers that opportunity to the user.
   */
  CONNECT_TO_HUMAN_AGENT = "connect_to_agent",

  /**
   * Displays an image to the user.
   */
  IMAGE = "image",

  /**
   * Indicates that the chat should display a pause at this point in the conversation before displaying additional
   * items.
   */
  PAUSE = "pause",

  /**
   * A user defined response will be displayed according to custom logic in the client.
   */
  USER_DEFINED = "user_defined",

  /**
   * Displays the contents of an iframe to the user.
   */
  IFRAME = "iframe",

  /**
   * Displays a video to the user using a video player.
   */
  VIDEO = "video",

  /**
   * Displays an audio clip to the user using an audio player.
   */
  AUDIO = "audio",

  /**
   * Asks the user to provide a date. This may result in a date picker being presented to the user.
   */
  DATE = "date",

  /**
   * Displays a general error message to the user and include developer info to be logged and to debug.
   */
  INLINE_ERROR = "inline_error",

  /**
   * Displays a card that can contain other response types.
   */
  CARD = "card",

  /**
   * Displays a carousel of cards that can contain other response types.
   */
  CAROUSEL = "carousel",

  /**
   * Displays a button that can either send a message back to the backend, open a url, or throw a client side event.
   */
  BUTTON = "button",

  /**
   * Ability to layout response types inside a grid.
   */
  GRID = "grid",

  /**
   * Ability to show citations on your RAG result.
   */
  CONVERSATIONAL_SEARCH = "conversational_search",

  /**
   * Displays a preview card that can take the user flow to a workspace view.
   */
  PREVIEW_CARD = "preview_card",

  /**
   * Displays a system message to the user. System messages appear centered between other messages
   * and are styled differently to indicate they are not from the user or assistant.
   *
   * If a response contains ONLY system messages, they render standalone (no avatar, no bubble).
   * If mixed with other response types, system messages render inline within the message bubble.
   *
   * @example
   * ```typescript
   * // Standalone system message
   * {
   *   output: {
   *     generic: [{
   *       response_type: "system",
   *       text: "Processing your request...",
   *     }]
   *   }
   * }
   *
   * // Inline system message (mixed with other content)
   * {
   *   output: {
   *     generic: [
   *       { response_type: "text", text: "Starting analysis..." },
   *       { response_type: "system", text: "Processing 1000 records" },
   *       { response_type: "text", text: "Analysis complete!" }
   *     ]
   *   }
   * }
   * ```
   */
  SYSTEM = "system",
}

/**
 * These are the human agent specific message types.
 *
 * @category Service desk
 */
export enum HumanAgentMessageType {
  /**
   * There was an error in a message.
   */
  INLINE_ERROR = "inline_error",

  /**
   * The agent sent a message.
   */
  FROM_HUMAN_AGENT = "from_agent",

  /**
   * The user sent a message.
   */
  FROM_USER = "from_user",

  /**
   * The agent left the chat.
   */
  HUMAN_AGENT_LEFT_CHAT = "agent_left_chat",

  /**
   * The agent ended the conversation.
   */
  HUMAN_AGENT_ENDED_CHAT = "agent_ended_chat",

  /**
   * The agent joined the conversation.
   */
  HUMAN_AGENT_JOINED = "agent_joined",

  /**
   * A disconnection warning was emitted.
   */
  RELOAD_WARNING = "user_connected_warning",

  /**
   * The conversation was transferred to another agent.
   */
  TRANSFER_TO_HUMAN_AGENT = "transfer_to_agent",

  /**
   * The end user ended the conversation with the agent.
   */
  USER_ENDED_CHAT = "user_ended_chat",

  /**
   * The conversation was ended.
   */
  CHAT_WAS_ENDED = "chat_was_ended",

  /**
   * The conversation was disconnected.
   */
  DISCONNECTED = "disconnected",

  /**
   * The conversation was re-connected.
   */
  RECONNECTED = "reconnected",

  /**
   * Screen sharing requested.
   */
  SHARING_REQUESTED = "sharing_requested",

  /**
   * Screen sharing accepted.
   */
  SHARING_ACCEPTED = "sharing_accepted",

  /**
   * Screen sharing declined.
   */
  SHARING_DECLINED = "sharing_declined",

  /**
   * Screen sharing cancelled.
   */
  SHARING_CANCELLED = "sharing_cancelled",

  /**
   * Screen sharing ended.
   */
  SHARING_ENDED = "sharing_ended",

  /**
   * A system message.
   */
  SYSTEM = "system",
}

/**
 * A general type to indicate any message.
 *
 * @category Messaging
 */
export type Message =
  | MessageRequest<MessageInput>
  | MessageRequest<EventInput>
  | MessageResponse;

/**
 * @category Messaging
 */
export interface ItemStreamingMetadata {
  /**
   * An identifier for this item within the full message response. This ID is used to correlate a partial or
   * complete item chunk with other chunks that represent the same item. This ID is only unique for a given message
   * response.
   */
  id: string;

  /**
   * When included on a partial_item, indicates if the stream can be cancelled.
   * If so, a "stop streaming" button will display in the UI.
   */
  cancellable?: boolean;

  /**
   * Indicates if the stream has stopped which will trigger the UI to respond with appropriate a11y states
   * and messaging.
   */
  stream_stopped?: boolean;
}

/**
 * Status of the chain of thought step.
 *
 * @category Messaging
 */
export { ChainOfThoughtStepStatus };

/**
 * A chain of thought step is meant to show tool calls and other steps made by your agent
 * to reach its final answer.
 *
 * @category Messaging
 */
export interface ChainOfThoughtStep {
  /**
   * The plain text name of the step.
   */
  title?: string;

  /**
   * An optional human readable description of what the tool does.
   *
   * Accepts markdown formatted text.
   */
  description?: string;

  /**
   * The plain text name of the tool called.
   */
  tool_name?: string;

  /**
   * Optional request metadata sent to a tool.
   */
  request?: {
    /**
     * Arguments sent to the tool. If this is properly formed JSON, it will be shown as a code block.
     */
    args?: unknown;
  };

  /**
   * Optional response from a tool.
   */
  response?: {
    /**
     * Content returned by the tool. If this is properly formed JSON, it will be shown as a code block.
     *
     * You can also return markdown compatible text here.
     */
    content: unknown;
  };

  /**
   * Optionally, share the status of this step. An icon will appear in the view showing the status. If no status is
   * shared, the UI will assume success.
   */
  status?: ChainOfThoughtStepStatus;
}

/**
 * Options that control additional features available for a message item.
 *
 * @category Messaging
 */
export interface GenericItemMessageOptions {
  /**
   * Controls the display of feedback options (thumbs up/down) for a message item.
   */
  feedback?: GenericItemMessageFeedbackOptions;

  /**
   * Optional configuration for a custom footer slot. This allows you to add custom content to the footer of a message.
   */
  custom_footer_slot?: GenericItemCustomFooterSlotOptions;
}

/**
 * If you want to have different categories for positive and negative feedback, you can provide two different arrays.
 *
 * You may not provide one of the arrays. e.g. you want negative categories but don't care about positive categories.
 *
 * @category Messaging
 */
export interface GenericItemMessageFeedbackCategories {
  /**
   * List of strings for positive feedback categories.
   */
  positive?: string[];

  /**
   * List of strings for negative feedback categories.
   */
  negative?: string[];
}

/**
 * Controls the display of a feedback options (thumbs up/down) for a message item.
 *
 * @category Messaging
 */
export interface GenericItemMessageFeedbackOptions {
  /**
   * Indicates if a request for feedback should be displayed.
   */
  is_on?: boolean;

  /**
   * A unique identifier for this feedback. This is required for the feedback to be recorded in message history.
   */
  id?: string;

  /**
   * The maximum number of characters allowed in the feedback text area.
   * defaults to 1000.
   */
  max_length?: number;

  /**
   * Indicates if the user should be asked for additional detailed information when providing positive feedback. This
   * defaults to true.
   */
  show_positive_details?: boolean;

  /**
   * Indicates if the user should be asked for additional detailed information when providing negative feedback. This
   * defaults to true.
   */
  show_negative_details?: boolean;

  /**
   * Indicates whether the text area should be shown. This defaults to true.
   */
  show_text_area?: boolean;

  /**
   * Indicates whether the prompt line should be shown. This defaults to true.
   */
  show_prompt?: boolean;

  /**
   * The title to display in the popup. A default value will be used if no value is provided here.
   */
  title?: string;

  /**
   * The prompt text to display to the user. A default value will be used if no value is provided here.
   */
  prompt?: string;

  /**
   * An optional set of categories to allow the user to choose from. This can either be an array of strings for
   * both positive and negative feedback or a {@link GenericItemMessageFeedbackCategories} object to make different
   * configuration for both.
   */
  categories?: string[] | GenericItemMessageFeedbackCategories;

  /**
   * The placeholder to show in the text area. A default value will be used if no value is provided here.
   */
  placeholder?: string;

  /**
   * The legal disclaimer text to show at the bottom of the popup. This text may contain rich markdown content. If this
   * value is not provided, no text will be shown.
   */
  disclaimer?: string;

  /**
   * The label text to display with the legal disclaimer checkbox. If this value is not provided, no disclaimer checkbox
   * or label text will be displayed.
   */
  disclaimerCheckbox?: string;
}

/**
 * Options that control the custom content in a message footer.
 *
 * @category Messaging
 */
export interface GenericItemCustomFooterSlotOptions {
  /**
   * Unique identifier for this footer slot. Used to match with the render function.
   */
  slot_name: string;

  /**
   * Indicates whether to show the custom footer slot. This defaults to true.
   */
  is_on?: boolean;

  /**
   * Optional additional data to pass to the render function. This can contain
   * any custom metadata that the frontend needs to render the footer appropriately.
   */
  additional_data?: Record<string, unknown>;
}

/**
 * @category Messaging
 */
export type PanelItem<TUserDefinedType = Record<string, unknown>> =
  BaseGenericItem<TUserDefinedType> & MessageItemPanelInfo;

/**
 * @category Messaging
 */
export type PartialOrCompleteItemChunk = PartialItemChunk | CompleteItemChunk;

/**
 * The base interface that all message response items must implement. Contains common properties
 * shared by all item types.
 *
 * @category Messaging
 */
interface BaseGenericItem<TUserDefinedType = Record<string, unknown>> {
  /**
   * The response type of this message item.
   */
  response_type: MessageResponseTypes;

  /**
   * Metadata used to identify a generic item within the context of a stream in order to correlate any updates meant
   * for a specific item.
   */
  streaming_metadata?: ItemStreamingMetadata;

  /**
   * An optional buckets of additional user defined properties for this item.
   */
  user_defined?: TUserDefinedType;

  /**
   * For messages that are sent between the user and a human agent, we assign an agent type to the message to distinguish what type it is.
   */
  agent_message_type?: HumanAgentMessageType;

  /**
   * Options that control additional features available for a message item.
   */
  message_item_options?: GenericItemMessageOptions;
}

/**
 * The basic class for items returned from an assistant as part of a message response. These are the items contained
 * in the {@link MessageOutput.generic} array.
 *
 * @category Messaging
 */
type GenericItem<TUserDefinedType = Record<string, unknown>> =
  | TextItem<TUserDefinedType>
  | OptionItem<TUserDefinedType>
  | ConnectToHumanAgentItem<TUserDefinedType>
  | ImageItem<TUserDefinedType>
  | PauseItem<TUserDefinedType>
  | UserDefinedItem<TUserDefinedType>
  | IFrameItem<TUserDefinedType>
  | VideoItem<TUserDefinedType>
  | AudioItem<TUserDefinedType>
  | DateItem<TUserDefinedType>
  | InlineErrorItem<TUserDefinedType>
  | CardItem<TUserDefinedType>
  | CarouselItem<TUserDefinedType>
  | ButtonItem<TUserDefinedType>
  | GridItem<TUserDefinedType>
  | ConversationalSearchItem<TUserDefinedType>
  | PreviewCardItem<TUserDefinedType>
  | SystemMessageItem<TUserDefinedType>;

/**
 * A user defined item returned in a message response from an assistant.
 *
 * @category Messaging
 */
interface UserDefinedItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * If the user_defined response type should be rendered as full width and ignore margin on the "start".
   */
  full_width?: boolean;
}

/**
 * This message item represents a preview card that can trigger a workflow view.
 *
 * @category Messaging
 */
interface PreviewCardItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The id of the workspace that is attached to this card.
   */
  workspace_id: string;

  /**
   * The title of the preview card.
   */
  title?: string;

  /**
   * The subtitle of the preview card.
   */
  subtitle?: string;

  /**
   * Options for opening the workspace.
   */
  workspace_options?: WorkspaceCustomPanelConfigOptions;

  /**
   * Additional data to be passed to workspace.
   */
  additional_data?: unknown;
}

/**
 * A text item returned in a message response from an assistant.
 *
 * The Carbon AI Chat supports basic styling inside text responses to match the theme of your Carbon AI Chat,
 * both with Markdown or HTML content returned from your assistant. Using Markdown and `user_defined`
 * ({@link UserDefinedItem}) responses instead of HTML in your text responses is the recommendation. It allows
 * adding channels that do not support HTML (such as Facebook, Slack, or WhatsApp) without having to rewrite
 * your content.
 *
 * ## Markdown
 *
 * The Carbon AI Chat supports the following Markdown syntax in the text response type:
 *
 * **Text formatting:**
 *
 * - `**bold text**` or `__bold text__`
 * - `*italic text*` or `_italic text_`
 * - `~~strikethrough~~`
 * - `==highlighted text==`
 * - `^superscript^`
 * - `~subscript~`
 *
 * **Code:**
 *
 * - `` `inline code` `` or fenced code blocks with syntax highlighting.
 *
 * **Headers:**
 *
 * - `# H1`, `## H2`, `### H3`, `#### H4`, `##### H5`, `###### H6`
 *
 * **Lists:**
 *
 * - Unordered lists using `*`, `+`, or `-`
 * - Ordered lists using `1.`, `2.`, etc.
 * - Nested lists are supported
 *
 * **Links and images:**
 *
 * - `[link text](URL)` for links (opens in new tab by default)
 * - `![alt text](image URL)` for images
 *
 * **Other elements:**
 *
 * - `> blockquote text` for blockquotes
 * - Tables using pipe syntax with automatic pagination and sorting
 * - Horizontal rules using `---` or `***`
 * - Line breaks are preserved (breaks: true)
 * - Automatic URL detection and conversion to links
 *
 * **Attributes:**
 *
 * - Custom attributes using `{{class="my-class" id="my-id"}}` syntax
 * - Supported attributes: `target`, `rel`, `class`, `id`
 *
 * **HTML support:**
 *
 * - Raw HTML is supported when enabled
 * - Custom elements and web components are allowed
 * - Content is sanitized for security when sanitization is enabled
 *
 * The Carbon AI Chat follows CommonMark rules with these extensions and enhancements.
 *
 * ## HTML content
 *
 * If you include HTML (including `style` and `script` tags) in your text response from your assistant, the
 * Carbon AI Chat renders those elements as provided, unless you set {@link PublicConfig.shouldSanitizeHTML}
 * to `true`. A better approach is to use a `user_defined` response instead of adding HTML directly to your
 * responses to make adding support for channels that do not support HTML easier.
 *
 * @category Messaging
 */
interface TextItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The text of the response.
   */
  text?: string;
}

/**
 * Visual layout for a {@link SystemMessageItem}.
 *
 * @category Messaging
 */
export type SystemMessageVariant = "default" | "date" | "agent";

/**
 * A system message item that can be returned in a message response. System messages are used for
 * status updates, progress indicators, or informational notices.
 *
 * If a response contains ONLY system messages, they render standalone (centered, no avatar, no bubble).
 * If mixed with other response types, system messages render inline within the message bubble.
 *
 * @category Messaging
 */
interface SystemMessageItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The response type is always "system" for system messages.
   */
  response_type: MessageResponseTypes.SYSTEM;

  /**
   * The title text to display in the system message.
   */
  title: string;

  /**
   * How the system line is presented when the message renders as a **standalone** system line
   * (response contains only system items): default helper text, date separator with rules, or
   * agent with a rule above and helper text. When system items render **inline** inside a
   * bubble, `variant` is ignored and the default inline style is used.
   * @default 'default'
   */
  variant?: SystemMessageVariant;
}

/**
 * A "connect to agent" item returned in a message response from an assistant. This is used when the back-end
 * indicates that a user's conversation should be escalated to a human agent.
 *
 * @category Messaging
 */
interface ConnectToHumanAgentItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * A message to be sent to the human agent who will be taking over the conversation.
   */
  message_to_human_agent?: string;

  /**
   * Contains the message to be rendered if there are agents available.
   */
  agent_available?: {
    message: string;
  };

  /**
   * Contains the message to be rendered if there are no agents available.
   */
  agent_unavailable?: {
    message: string;
  };

  /**
   * When a conversation is escalated to an agent additional information is needed to fulfill the request. This
   * additional information typically is added by the channel integration and cannot be deduced from the dialog
   * itself.
   */
  transfer_info?: ConnectToHumanAgentItemTransferInfo;
}

/**
 * Additional information as part of a {@link ConnectToHumanAgentItem} that may be needed to perform a transfer to an agent.
 *
 * @category Messaging
 */
interface ConnectToHumanAgentItemTransferInfo {
  /**
   * Each service desk may require different information to start the connection. It can be account details or
   * security information. This is a bucket of all the service desk specific properties.
   */
  additional_data?: {
    [key: string]: string;
  };

  /**
   * An initial set of message items to send to the agent.
   */
  summary_message_to_agent?: TextItem[];
}

/**
 * A pause item returned in a message response from an assistant. This indicates that the client should pause before
 * displaying additional response items.
 *
 * @category Messaging
 */
interface PauseItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * How long to pause, in milliseconds.
   */
  time?: number;

  /**
   * Whether to display an "is typing" indicator during the pause.
   */
  typing?: boolean;
}

/**
 * An option item returned in a message response from an assistant. This response type is used when displaying a list
 * of options to the user. How the options are displayed is up to the client but is often displayed in either a
 * drop-down or as a list of buttons.
 *
 * @category Messaging
 */
interface OptionItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * An array of objects describing the options from which the user can choose.
   */
  options: SingleOption[];

  /**
   * An optional title to be shown alongside the options.
   */
  title?: string;

  /**
   * An optional description to be shown alongside the options.
   */
  description?: string;

  /**
   * The preferred type of control to display (e.g. button or dropdown).
   */
  preference?: OptionItemPreference;
}

/**
 * The set of possible response preferences for an options response.
 *
 * @category Messaging
 */
enum OptionItemPreference {
  /**
   * Indicates the options should be displayed as a drop-down.
   */
  DROPDOWN = "dropdown",

  /**
   * Indicates the options should be displayed as buttons.
   */
  BUTTON = "button",
}

/**
 * Represents an individual option that is part of an "options" response.
 *
 * @category Messaging
 */
interface SingleOption {
  /**
   * The user-facing label for the option or disambiguation suggestion. This label is taken from the user_label property
   * of the corresponding dialog node.
   */
  label: string;

  value: {
    /**
     * An input object that should be sent back to the assistant when this option is chosen by a user.
     */
    input: MessageInput;
  };
}

/**
 * @category Messaging
 */
interface IFrameItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The source URL to an embeddable page
   */
  source: string;

  /**
   * The preview image of the source URL. This property is unfurled from the source URL at runtime. It is used when
   * IFrameItemDisplayOption is set to 'panel' for the preview card to open the panel.
   */
  image_url?: string;

  /**
   * The title of the source URL. This property is unfurled from the source URL at runtime. It is used when
   * IFrameItemDisplayOption is set to 'panel' for the preview card to open the panel.
   */
  title?: string;

  /**
   * The description of the source URL. This property is unfurled from the source URL at runtime. It is used when
   * IFrameItemDisplayOption is set to 'panel' for the preview card to open the panel.
   */
  description?: string;

  /**
   * How the iframe should be displayed.
   */
  display?: IFrameItemDisplayOption;
}

/**
 * Dimension information for displaying a media item.
 *
 * @category Messaging
 */
interface MediaItemDimensions {
  /**
   * This property's value is used to calculate a responsive height for Carbon AI Chat's media player so that its aspect
   * ratio is the same between different screen widths. This is set to a reasonable default depending on the response type
   * and other details like what service you are pulling the content from (e.g. Youtube or SoundCloud).
   */
  base_height?: number;
}

/**
 * Represents a single subtitle/caption track for video content.
 * Uses WebVTT format for accessibility. Rendered as native HTML5 track elements.
 *
 * @category Messaging
 */
interface MediaSubtitleTrack {
  /**
   * URL pointing to the WebVTT subtitle file.
   */
  src: string;

  /**
   * The language code (e.g., "en", "es", "fr").
   * Used for the track's srclang attribute.
   */
  language: string;

  /**
   * Human-readable label for the track (e.g., "English", "Spanish").
   * Displayed in the browser's subtitle menu.
   */
  label: string;

  /**
   * The kind of text track.
   * - "subtitles": Translation of dialogue (default)
   * - "captions": Transcription including sound effects
   * - "descriptions": Audio descriptions for visually impaired
   */
  kind?: "subtitles" | "captions" | "descriptions";

  /**
   * Whether this track should be enabled by default.
   * Only one track should be default.
   */
  default?: boolean;
}

/**
 * Represents a text transcript for audio content.
 * Displayed as readable text below the audio player for accessibility.
 *
 * @category Messaging
 */
interface MediaTranscript {
  /**
   * Full text transcript of the audio content.
   * Supports markdown for formatting.
   */
  text: string;

  /**
   * Language of the transcript (e.g., "en", "es", "fr").
   */
  language?: string;

  /**
   * Optional label for the transcript (e.g., "English Transcript").
   */
  label?: string;
}

/**
 * Accessibility features for raw media files (not embedded platforms).
 * These features only apply when using direct file URLs (e.g., .mp4, .mp3).
 *
 * For embedded platforms (YouTube, Vimeo, SoundCloud, etc.),
 * rely on the platform's built-in accessibility features instead.
 *
 * @category Messaging
 */
interface MediaFileAccessibility {
  /**
   * Subtitle/caption tracks for video files.
   * Supports WebVTT format rendered as native HTML5 track elements.
   */
  subtitle_tracks?: MediaSubtitleTrack[];

  /**
   * Text transcript for audio files.
   * Displayed as expandable text below the audio player.
   */
  transcript?: MediaTranscript;
}

/**
 * The different ways an iframe item may be displayed.
 *
 * @category Messaging
 */
enum IFrameItemDisplayOption {
  /**
   * The iframe is displayed inline in the main message list.
   */
  INLINE = "inline",

  /**
   * The iframe is displayed in a separate panel after showing a card in the main message list.
   */
  PANEL = "panel",
}

/**
 * A reusable media object that may need to display a title and description with an alt_text to label the item for
 * accessibility purposes. This is used by the Audio, Video and Image response types.
 *
 * @category Messaging
 */
interface MediaItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The url pointing to a media source, whether audio, video, or image.
   *
   * For video this can be a file like an .mp4 or a YouTube, Facebook, Vimeo, Twitch, Streamable, Wistia, or Vidyard url.
   *
   * For audio this can be a file like an .mp3 or a SoundCloud url.
   */
  source: string;

  /**
   * The title for the item.
   */
  title?: string;

  /**
   * The description for the item.
   */
  description?: string;

  /**
   * The alt text for labeling the item. Screen readers will announce this text when the user's virtual cursor
   * is focused on the item.
   */
  alt_text?: string;

  /**
   * Settings that control the dimensions for the media item.
   */
  dimensions?: MediaItemDimensions;

  /**
   * Accessibility features for raw media files.
   * Only applies to direct file URLs (e.g., .mp4, .mp3), not embedded platforms.
   */
  file_accessibility?: MediaFileAccessibility;
}

/**
 * Citations for text generated by an AI to provide the user with relevant source information and context.
 *
 * @category Messaging
 */
interface ConversationalSearchItemCitation {
  /**
   * Optional url of the citation. May not be a valid URL.
   */
  url?: string;

  /**
   * Optional explanation text for the citation.
   */
  text?: string;

  /**
   * Optional title of the citation URL.
   */
  title?: string;

  /**
   * Optional array of ranges indicating where in `text` the citation is located.
   */
  ranges?: {
    start: number;
    end: number;
  }[];

  /**
   * In some implementations, the content searched isn't in an accessible URL. For instance, it could be from Milvus or
   * ElasticSearch. In that scenario, you may populate "search_results". This field will allow you define which index in
   * the search_results array matches with this citation. The end user can then drill into the larger search result to
   * view it rather than clicking on a URL to actually see the content.
   */
  search_result_idx?: number;
}

/**
 * A text response generated by AI with an optional list of citations for where the information came from.
 *
 * @category Messaging
 */
interface ConversationalSearchItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The returned conversational text. Any HTML/Markdown will be ignored.
   */
  text: string;

  /**
   * Citations are used to connect specific text within a conversational search response with the relevant documents
   * returned by the backend.
   */
  citations?: ConversationalSearchItemCitation[];

  /**
   * In some implementations, the content searched isn't in an accessible URL. For instance, it could be from Milvus or
   * ElasticSearch. In that scenario, you may populate "search_results". Combine this with
   * {@link ConversationalSearchItemCitation.search_result_idx}.
   */
  search_results?: SearchResult[];
}

/**
 * An inline error response generated by a conversational skill provider with a user-friendly text and developer debug info.
 *
 * @category Messaging
 */
interface InlineErrorItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * Some end user friendly text describing the error and what they should do next.
   *
   * If no specific text is passed, the client is responsible for fallback generic error message text.
   */
  text?: string;

  /**
   * Relevant debug info intended to be passed on to developers.
   *
   * This information should not include anything sensitive that might reveal details about our back-end environment that should not be public.
   */
  debug?: {
    /**
     * The error code of any underlying error, despite the service returning 200.
     */
    statusCode?: number;

    /**
     * Developer-friendly error text.
     */
    text?: string;

    /**
     * Any additional key-value pairs for debugging.
     */
    info?: Record<string, unknown>;
  };
}

/**
 * The image response type definition. This is currently the same as {@link MediaItem}.
 *
 * @category Messaging
 */
type ImageItem<TUserDefinedType = Record<string, unknown>> =
  MediaItem<TUserDefinedType>;

/**
 * The video response type definition for future reuse. This is currently the same as {@link MediaItem}.
 *
 * @category Messaging
 */
type VideoItem<TUserDefinedType = Record<string, unknown>> =
  MediaItem<TUserDefinedType>;

/**
 * The audio response type definition for future reuse. This is currently the same as {@link MediaItem}.
 *
 * @category Messaging
 */
type AudioItem<TUserDefinedType = Record<string, unknown>> =
  MediaItem<TUserDefinedType>;

/**
 * @category Messaging
 */
enum ButtonItemType {
  /**
   * A button that sends its value back to the backend.
   */
  POST_BACK = "post_back",

  /**
   * A button that throws an event for your UI to respond to.
   */
  CUSTOM_EVENT = "custom_event",

  /**
   * A button that shows a panel.
   */
  SHOW_PANEL = "show_panel",

  /**
   * A button that opens a URL.
   */
  URL = "url",
}

/**
 * @category Messaging
 */
enum WidthOptions {
  /**
   * Width the size of the floating chat for smaller content.
   */
  SMALL = "small",

  /**
   * Max width of 438px, 2/3rd of the width of chat in fullscreen view with hasContentMaxWidth: true.
   */
  MEDIUM = "medium",

  /**
   * Max width of 585px, the full with of chat in fullscreen view with hasContentMaxWidth: true.
   */
  LARGE = "large",
}

/**
 * @category Messaging
 */
interface WithWidthOptions {
  /**
   * Sets an optional max width of the component. Options are small, medium and large.
   * By default, the component will be 100% width of the container.
   */
  max_width?: WidthOptions;
}

/**
 * @category Messaging
 */
interface WithBodyAndFooter {
  /**
   * A list of message items to render in a Carbon AI Chat panel.
   */
  body?: GenericItem[];

  /**
   * A list of button items that are rendered under the panel body.
   */
  footer?: ButtonItem[];
}

/**
 * @category Messaging
 */
interface MessageItemPanelInfo extends WithBodyAndFooter {
  /**
   * The title to give the panel in Carbon AI Chat.
   */
  title?: string;

  /**
   * Determines if the panel header should not be visible or not.
   *
   * Defaults to true.
   */
  show_header?: boolean;

  /**
   * Determines if the panel close and open animations should be enabled or not.
   *
   * Defaults to true.
   */
  show_animations?: boolean;

  /**
   * Shows the AI gradient background on your panel. Can be used with in concert with showFrame.
   *
   * Defaults to {@link PublicConfig.aiEnabled} value.
   */
  ai_enabled?: boolean;

  /**
   * Show a frame with the chat shell background instead of the gradient background for your panel content.
   *
   * Defaults to true.
   */
  show_frame?: boolean;

  /**
   * By default, the panel will render at the width of the messages list. If you want to be able to render to a full screen
   * width slot, set full_width to true.
   *
   * Defaults to false.
   */
  full_width?: boolean;
}

/**
 * @category Messaging
 */
enum ButtonItemKind {
  /**
   * Default Carbon button.
   */
  DEFAULT = "default",

  /**
   * Secondary Carbon button.
   */
  SECONDARY = "secondary",

  /**
   * Tertiary Carbon button.
   */
  TERTIARY = "tertiary",

  /**
   * Danger Carbon button.
   */
  DANGER = "danger",

  /**
   * Ghost Carbon button.
   */
  GHOST = "ghost",

  /**
   * Button displayed like a link.
   */
  LINK = "link",
}

/**
 * This message item represents a button that can perform various actions such as sending messages, opening URLs, or showing panels.
 *
 * @category Messaging
 */
interface ButtonItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  /**
   * The style of button to display.
   */
  kind?: BUTTON_KIND | CHAT_BUTTON_KIND | "LINK";

  /**
   * The button size.
   */
  size?: BUTTON_SIZE | CHAT_BUTTON_SIZE;

  /**
   * Whether the button should be rendered as a standard carbon button.
   *
   * @internal
   */
  is?: "standard-button";

  /**
   * The type of button.
   */
  button_type: ButtonItemType;

  /**
   * The URL for the user to visit when the button is clicked.
   */
  url?: string;

  /**
   * Where to open the link. The default value is _self.
   */
  target?: string;

  /**
   * The display text for the link.
   */
  label?: string;

  /**
   * A custom event that can be listened to by Carbon AI Chat when the button item is clicked.
   */
  custom_event_name?: string;

  value?: {
    /**
     * An input object that should be sent back to the assistant when this option is chosen by a user.
     */
    input: MessageInput;
  };

  /**
   * When true (post_back buttons only), the request generated by clicking this button is sent to
   * `customSendMessage` but not added to the visible chat transcript. Use this when the button's
   * `value.input.text` is a machine-readable trigger string that you don't want the user to see.
   *
   * @default false
   */
  silent?: boolean;

  /**
   * The panel options to display in a panel when the "show_panel" button type is clicked.
   */
  panel?: MessageItemPanelInfo;

  /**
   * The URL pointing to an image.
   */
  image_url?: string;

  /**
   * The alt text for labeling the item. Screen readers will announce this text when the user's virtual cursor
   * is focused on the item.
   */
  alt_text?: string;
}

/**
 * @category Messaging
 */
type CardItem<TUserDefinedType = Record<string, unknown>> =
  BaseGenericItem<TUserDefinedType> & WithBodyAndFooter & WithWidthOptions;

/**
 * @category Messaging
 */
interface CarouselItem<
  TUserDefinedType = Record<string, unknown>,
> extends BaseGenericItem<TUserDefinedType> {
  items: GenericItem[];
}

/**
 * Horizontal alignment values for items in a grid response.
 *
 * @category Messaging
 */
type HorizontalCellAlignment = "left" | "center" | "right";

/**
 * Vertical alignment values for items in a grid response.
 *
 * @category Messaging
 */
type VerticalCellAlignment = "top" | "center" | "bottom";

/**
 * @category Messaging
 */
interface GridItem<TUserDefinedType = Record<string, unknown>>
  extends BaseGenericItem<TUserDefinedType>, WithWidthOptions {
  /**
   * Determines the horizontal alignment of all items in the grid.
   */
  horizontal_alignment?: HorizontalCellAlignment;

  /**
   * Determines the vertical alignment of all items in the grid.
   */
  vertical_alignment?: VerticalCellAlignment;

  /**
   * The list of column specifications. This will determine the maximum number of columns that can be rendered.
   */
  columns: {
    width: string;
  }[];

  /**
   * A list of rows to render.
   */
  rows: {
    /**
     * A list of cells to render in a row.
     */
    cells: {
      /**
       * Determines the horizontal alignment of items in the individual cell.
       */
      horizontal_alignment?: HorizontalCellAlignment;

      /**
       * Determines the vertical alignment of items in the individual cell.
       */
      vertical_alignment?: VerticalCellAlignment;

      /**
       * Message items to render in the cell.
       */
      items: GenericItem[];
    }[];
  }[];
}

/**
 * This is the response item that represents a request for a date which should prompt the client to use a date picker or
 * similar control to provide a date. There are currently no additional properties of the response.
 *
 * @category Messaging
 */
type DateItem<TUserDefinedType = Record<string, unknown>> =
  BaseGenericItem<TUserDefinedType>;

/**
 * @category Messaging
 */
interface Chunk {
  /**
   * Additional metadata associated with the stream.
   */
  streaming_metadata?: {
    /**
     * The ID of the complete message response object. This ID will be the ID of the full message that is received
     * in the final chunk of the stream.
     */
    response_id: string;
  };
}

/**
 *
 * @category Messaging
 * @internal
 */
export interface MessageUIStateInternal {
  /**
   * @internal
   * Indicates if this message was loaded from history or if it's a new message created in the current browser
   * page.
   */
  from_history?: boolean;

  /**
   * @internal
   * The state of a connect to agent card when it's initially displayed. Used by session history on reload to make
   * sure the initial state is persisted. This property can apply to all CTA responses in the message should there
   * happen to be more than one. When this property is null it means a check was performed but it got no answer.
   */
  agent_availability?: HumanAgentsOnlineStatus;

  /**
   * @internal
   * This is used by the connect to agent message. It indicates that no service desk was configured when the connect
   * to agent response was received by the client.
   */
  agent_no_service_desk?: boolean;
}

/**
 * If the reasoning step is open, closed, or is controlled by Carbon AI Chat.
 *
 * If a user elects to open/close the user action will override what is provided here.
 *
 * @category Messaging
 */
enum ReasoningStepOpenState {
  OPEN = "open",
  CLOSE = "close",
  DEFAULT = "default",
}

/**
 * An individual reasoning step.
 *
 * @category Messaging
 */
interface ReasoningStep {
  /**
   * The title of the reasoning step.
   */
  title: string;

  /**
   * Marks if this individual step is open. Only use this if you don't want the default behavior.
   *
   * If the step has content, by default the reasoning step will automatically open and will close when the
   * next step(s) have content or the first {@link GenericItem} is returned with something to display.
   *
   * No matter what you choose, if the user manually marks something open/closed they retain control.
   */
  open_state?: ReasoningStepOpenState;

  /**
   * Optional markdown content (string) or a list of {@link GenericItem} response items to render inside this
   * reasoning step. When an array is supplied, each item is rendered through the standard message renderer.
   *
   * GenericItem variants that render purely from their own item data (TextItem, ImageItem, UserDefinedItem,
   * ButtonItem, InlineErrorItem, etc.) are fully supported. Variants whose renderers rely on nested-body
   * hydration (Card, Carousel, Grid bodies) will not hydrate those nested children when used here.
   */
  content?: string | GenericItem[];
}

/**
 * The interface describing how to pass reasoning steps to the UI.
 *
 * @category Messaging
 */
interface ReasoningSteps {
  /**
   * Marks if the reasoning step interface is open. Only use this if you don't want the default behavior.
   *
   * By default the reasoning step interface will automatically open and will then close when the first
   * {@link GenericItem} is returned with something to display.
   *
   * No matter what you choose, if the user manually marks something open/closed they retain control.
   */
  open_state?: ReasoningStepOpenState;

  /**
   * The array of reasoning steps for this message.
   */
  steps?: ReasoningStep[];

  /**
   * Optional markdown content (string) or a list of {@link GenericItem} response items to render as a preamble
   * above the reasoning steps. When an array is supplied, each item is rendered through the standard message
   * renderer.
   *
   * GenericItem variants that render purely from their own item data (TextItem, ImageItem, UserDefinedItem,
   * ButtonItem, InlineErrorItem, etc.) are fully supported. Variants whose renderers rely on nested-body
   * hydration (Card, Carousel, Grid bodies) will not hydrate those nested children when used here.
   */
  content?: string | GenericItem[];
}

/**
 * This interface contains options for a {@link MessageResponse}.
 *
 * @category Messaging
 */
interface MessageResponseOptions {
  /**
   * This is the profile for the human or assistant who sent or triggered this message.
   */
  response_user_profile?: ResponseUserProfile;

  /**
   * Controls the display of the reasoning steps component.
   *
   * Most people should use reasoning steps instead of chain of thought.
   *
   * Chain of thought it meant more for technical "called X API and got Y result back".
   *
   * Reasoning steps can include that kind of detail depending on your use case, but is meant more for user friendly
   * content than debugging technical internal content.
   */
  reasoning?: ReasoningSteps;

  /**
   * Controls the display of the chain of thought component.
   *
   * Most people should use reasoning steps instead of chain of thought.
   *
   * Chain of thought it meant more for technical "called X API and got Y result back".
   *
   * Reasoning steps can include that kind of detail depending on your use case, but is meant more for user friendly
   * content than debugging technical internal content.
   */
  chain_of_thought?: ChainOfThoughtStep[];
}

/**
 * This interface contains information about the history of a given {@link MessageResponse}. This information should be
 * saved your history store.
 *
 * @category Messaging
 */
interface MessageResponseHistory {
  /**
   * The time at which this message occurred.
   */
  timestamp?: number;

  /**
   * Indicates if this is a "silent" message. These messages are sent to or received from the assistant but should
   * not be displayed to the user.
   */
  silent?: boolean;

  /**
   * The error state of this message.
   */
  error_state?: MessageErrorState;

  /**
   * The state of feedback provided on the items in this message.
   */
  feedback?: {
    [feedbackID: string]: MessageHistoryFeedback;
  };

  /**
   * @internal
   * If this message represents a file upload, this is the status of that file. If the upload failed due to an
   * error, the upload will be complete and the error_state value above will be set. The "success" status is a
   * temporary status that displays a checkmark on successful uploads. The "complete" status is the permanent
   * status stored in session history.
   */
  file_upload_status?: FileStatusValue;
}

/**
 * This interface contains information about the history of a given {@link MessageRequest}. This information should be
 * saved your history store.
 *
 * @category Messaging
 */
interface MessageRequestHistory {
  /**
   * The time at which this message occurred.
   */
  timestamp?: number;

  /**
   * The user-friendly label that was associated with this message. This is used on messages that were sent by the
   * user to the assistant to request a response. This is the user displayed text that was entered or selected by
   * the user when that request was made. Most commonly used to make sure a {@link OptionItem} shows the correct button
   * selected when loading history.
   */
  label?: string;

  /**
   * If the message was a welcome node request.
   */
  is_welcome_request?: boolean;

  /**
   * If this message is related to another message, this is the ID of that other message. This is used when a user
   * choices an option and it includes the ID of the message response that presented the options to the user so we
   * can associate the user's request with that earlier response and display the appropriate selected state.
   */
  related_message_id?: string;

  /**
   * Indicates if this is a "silent" message. These messages are sent to or received from the assistant but should
   * not be displayed to the user.
   */
  silent?: boolean;

  /**
   * The error state of this message.
   */
  error_state?: MessageErrorState;

  /**
   * @internal
   * If this message represents a file upload, this is the status of that file. If the upload failed due to an
   * error, the upload will be complete and the error_state value above will be set. The "success" status is a
   * temporary status that displays a checkmark on successful uploads. The "complete" status is the permanent
   * status stored in session history.
   */
  file_upload_status?: FileStatusValue;
}

/**
 * @category Messaging
 */
interface MessageHistoryFeedback {
  /**
   * Indicates if positive feedback was provided.
   */
  is_positive: boolean;

  /**
   * The feedback text provided by the user.
   */
  text?: string;

  /**
   * When submitting feedback details, this is the list of categories the user selected (if visible).
   */
  categories?: string[];
}

/**
 * @category Messaging
 */
interface PartialResponse {
  /**
   * This contains the history of this response.
   */
  message_options?: DeepPartial<MessageResponseOptions>;
}

/**
 * The interface for a chunk that represents a partial update (or first time chunk) to a message item.
 *
 * @category Messaging
 */
interface PartialItemChunk extends Chunk {
  /**
   * The partial details of the item. The client will decide what rules to follow for merging this in with any
   * existing data for the same item (which is identified using the {@link ItemStreamingMetadata.id} property).
   */
  partial_item: DeepPartial<GenericItem>;

  /**
   * Change the agent display name and other items on the full response.
   */
  partial_response?: PartialResponse;
}

/**
 * A stricter partial item chunk type for streaming implementations that want compile-time enforcement of
 * {@link ItemStreamingMetadata.id}. This is optional and not required for compatibility.
 *
 * @category Messaging
 */
type PartialItemChunkWithId = Omit<PartialItemChunk, "partial_item"> & {
  partial_item: DeepPartial<GenericItem> & {
    streaming_metadata: ItemStreamingMetadata;
  };
};

/**
 * Completes a single streamed item before the full response is ready.
 *
 * Use this to replace a streamed item with its final, corrected version while other
 * items are still streaming. The complete item should include all data needed to
 * render the item (including anything from partial chunks). Include
 * {@link ItemStreamingMetadata.id} to correlate with prior partial chunks and preserve
 * identity. If you are only streaming a single item, you can skip this and send a
 * {@link FinalResponseChunk} instead.
 *
 * @category Messaging
 */
interface CompleteItemChunk extends Chunk {
  /**
   * A complete message item. If this item was streamed via partial chunks,
   * you should include {@link ItemStreamingMetadata.id} so the UI can preserve identity.
   */
  complete_item: GenericItem;
  /**
   * Change the agent display name and other items on the full response.
   */
  partial_response?: PartialResponse;
}

/**
 * Finalizes the full response and ends streaming.
 *
 * This provides the authoritative final state of the full message response. It should
 * include all items that were streamed (and any corrections). For any item that was
 * streamed, include {@link ItemStreamingMetadata.id} to preserve identity and avoid
 * remounts. The message ID should match streaming_metadata.response_id.
 *
 * @category Messaging
 */
interface FinalResponseChunk {
  /**
   * The final message response. If this response contains items that were streamed,
   * those items should include {@link ItemStreamingMetadata.id} to avoid remounts.
   */
  final_response: MessageResponse;
}

/**
 * @category Messaging
 */
type StreamChunk = PartialItemChunk | CompleteItemChunk | FinalResponseChunk;

/**
 * Types of users we accept messages from.
 *
 * @category Messaging
 */
enum UserType {
  /**
   * A message from a human.
   */
  HUMAN = "human",

  /**
   * A message from a non-watsonx assistant, used for interacting with assistants that are not backed by watsonx.
   *
   * Official guidance is to not use this for IBM products without explicit exception.
   */
  BOT = "bot",

  /**
   * A message from watsonx.
   */
  WATSONX = "watsonx",
}

/**
 * Profile information about a specific agent that can be used to display information to the user. This may
 * represent a human agent or a virtual assistant agent.
 *
 * @category Messaging
 */
interface ResponseUserProfile {
  /**
   * A unique identifier for this agent.
   */
  id: string;

  /**
   * The visible name for the response author. Can be the full name or just a first name for a human.
   */
  nickname: string;

  /**
   * The type of user. If its a "human" there is more protection against code injection attacks, where as assistant responses
   * are trusted by default unless {@link PublicConfig.shouldSanitizeHTML} is set to true.
   */
  user_type: UserType;

  /**
   * A URL pointing to an avatar for the response author. This image should be a square.
   */
  profile_picture_url?: string;
}

/**
 * A single search result.
 *
 * @category Messaging
 */
interface SearchResult {
  /**
   * The search result. This can be drilled into and viewed in a larger and scrollable format.
   */
  body?: string;
}

export {
  ResponseUserProfile,
  AudioItem,
  BaseGenericItem,
  BaseMessageInput,
  ButtonItem,
  ButtonItemKind,
  ButtonItemType,
  CardItem,
  CarouselItem,
  Chunk,
  CompleteItemChunk,
  ConnectToHumanAgentItem,
  ConnectToHumanAgentItemTransferInfo,
  ConversationalSearchItem,
  ConversationalSearchItemCitation,
  DateItem,
  EventInput,
  EventInputData,
  FinalResponseChunk,
  GenericItem,
  GridItem,
  HorizontalCellAlignment,
  IFrameItem,
  IFrameItemDisplayOption,
  ImageItem,
  InlineErrorItem,
  MediaItem,
  MediaItemDimensions,
  MediaSubtitleTrack,
  MediaTranscript,
  MediaFileAccessibility,
  MessageInput,
  MessageInputType,
  MessageItemPanelInfo,
  MessageOutput,
  MessageRequest,
  MessageResponse,
  MessageResponseTypes,
  SingleOption,
  OptionItem,
  OptionItemPreference,
  PartialItemChunk,
  PartialItemChunkWithId,
  PauseItem,
  StreamChunk,
  TextItem,
  UserDefinedItem,
  VerticalCellAlignment,
  VideoItem,
  WithBodyAndFooter,
  WidthOptions,
  WithWidthOptions,
  MessageHistoryFeedback,
  InternalMessageRequestType,
  SearchResult,
  PartialResponse,
  SystemMessageItem,
  UserType,
  MessageResponseOptions,
  MessageResponseHistory,
  MessageRequestHistory,
  ReasoningSteps,
  ReasoningStep,
  ReasoningStepOpenState,
  PreviewCardItem,
  StructuredData,
  StructuredField,
  StructuredFieldType,
  StructuredFieldValue,
  InlineFile,
  ExternalFileReference,
  FileFieldValue,
};
